/**
 * First launch (docs/TUTORIAL.md §3.9): "NEW HERE?" over the main menu, once. ROOKIE CAMP · 4 MIN (default) starts
 * lesson 1; I KNOW FOOTBALL opens HOW TO PLAY; SKIP closes. Any choice marks the offer as shown.
 */
import './hub.css';
import { useEffect, useRef } from 'preact/hooks';
import { useActions, useServices } from '../actions';
import { Glove, Head } from '../components';
import { LAYER, useFocusGrid } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { openHowToPlay } from './HowToPlay';
import { progress, updateProgress } from '@hf/client-core/ui-model/tutorial/progress';
import { startTutorial } from './start';

export function NewHereOffer() {
  const actions = useActions();
  const { audio } = useServices();
  const items = [
    {
      id: 'camp',
      label: 'ROOKIE CAMP',
      sub: '5 LESSONS · ABOUT 4 MIN',
      run: () => {
        actions.unlockAudio();
        startTutorial('move', progress.peek().head, actions.startMatch);
      },
    },
    {
      id: 'know',
      label: 'I KNOW FOOTBALL',
      sub: 'JUST SHOW ME THE RULES',
      run: () => {
        updateProgress({ offered: true });
        openHowToPlay();
      },
    },
    { id: 'skip', label: 'SKIP', sub: 'STRAIGHT TO THE MENU', run: () => updateProgress({ offered: true }) },
  ];
  // The buttons sit 2 + 1 (ROOKIE CAMP · I KNOW FOOTBALL, SKIP under them): the focus moves the way they look
  // (▼ from either top button reaches SKIP; it used to be a horizontal list, so ▼ did nothing and ▶ wrapped to SKIP).
  const grid = useFocusGrid({
    count: items.length,
    cols: 2,
    wrap: false,
    priority: LAYER.MODAL,
    onEdge: (dir) => {
      if (dir === 'down' && grid.index === 1) grid.setIndex(2);
    },
    onMove: () => playSfx(audio, UI_SFX.move),
    onConfirm: (i) => {
      playSfx(audio, i === 2 ? UI_SFX.back : UI_SFX.start);
      items[i]?.run();
    },
    onBack: () => {
      playSfx(audio, UI_SFX.back);
      updateProgress({ offered: true });
    },
  });
  // DOM focus moves into the dialog (it stayed on the menu's EXHIBITION row behind the scrim until the first move:
  // the menu focuses its row after this dialog mounted)
  const btns = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const el = btns.current?.querySelector<HTMLElement>('[data-focused="true"]');
      if (el && document.activeElement !== el) el.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div class="hf-newhere" role="dialog" aria-modal="true" aria-label="New here?">
      <div class="hf-newhere__scrim" />
      <div class="hf-newhere__card">
        <div class="hf-newhere__art" aria-hidden="true">
          <Head character="rookie" expression="celebrating" class="hf-newhere__head" />
          <Glove side="home" pose="ready" class="hf-newhere__glove" />
        </div>
        <div class="hf-newhere__text">
          <div class="hf-newhere__kicker t-broadcast">FIRST TIME AT THE STADIUM?</div>
          <div class="hf-newhere__title t-display">NEW HERE?</div>
          <p>
            Never watched American football? Rookie Camp teaches the snap, tackles, downs, touchdowns and the
            pass by playing them, one button at a time.
          </p>
          <div class="hf-newhere__btns" ref={btns}>
            {items.map((it, i) => (
              <button key={it.id} class={`hf-btn ${i === 0 ? '' : 'dark'} interactive`} {...grid.item(i)}>
                <span>{it.label}</span>
                <small class="t-broadcast">{it.sub}</small>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
